import { useEffect } from "react"

import {
  EEventStatus,
  EVENT_STATUS,
  TRecentEventData,
} from "@letshang/constants"
import { debounce, findIndex, get, orderBy, uniqBy } from "lodash-es"
import moment from "moment"
import { useRecoilState } from "recoil"

import {
  allEventCacheState,
  eventDetailsState,
  recentViewEventData,
} from "@fuse/recoil/events"
import { userState } from "@fuse/recoil/signup"

import { getEventDetails } from "@api/firebaseWrappedApis"

const useFetchEventDetails = (eventId: string, parentScreen?: string) => {
  const [recentEvents, setRecentEvents] = useRecoilState(recentViewEventData)
  const [state, setState] = useRecoilState(eventDetailsState)
  const [user] = useRecoilState(userState)
  const [cache, setCache] = useRecoilState(allEventCacheState)
  const uid = get(user, "uid", "")

  const isLoading = get(state, "isLoading", false)
  const isCreateEvent = parentScreen === "createEvent"

  useEffect(() => {
    if (!eventId) return

    const fetchDebounced = debounce(() => {
      if (cache[eventId]) {
        // Use cached data immediately
        const cachedData = cache[eventId]

        setState({
          eventDetails: cachedData.eventDetails,
          eventType: cachedData.eventType,
          isLoading: false,
          hasError: cachedData.hasError,
          doesNotExist: false,
        })

        // Fetch in background to update cache
        fetchEventDetails(true)
      } else {
        fetchEventDetails(false)
      }
    }, 200)

    fetchDebounced()

    return () => fetchDebounced.cancel()
  }, [eventId])

  const getEventModeFromData = (data: any): EEventStatus => {
    if (data?.eventDocData?.timing?.signupCloseDate) {
      const signupCloseDate = moment(
        data.eventDocData.timing.signupCloseDate,
      ).unix()
      const now = moment().unix()
      if (now > signupCloseDate) {
        return EEventStatus.CLOSED
      }
    }
    return EEventStatus.UPCOMING
  }

  const handleAddEventToRecent = (event: TRecentEventData) => {
    // Check if any existing event is a draft
    const hasDraftEvent = recentEvents.some(
      (existingEvent) => existingEvent.eventStatus === EVENT_STATUS.DRAFT,
    )

    // to be removed after 1 month, else it will lead to unnecessary processing
    if (hasDraftEvent) {
      const filteredEvents = recentEvents.filter((existingEvent) => {
        return existingEvent.eventDocData.eventStatus !== EVENT_STATUS.DRAFT
      })
      setRecentEvents((prev) => [...filteredEvents])
    }

    const existingEventIndex = findIndex(recentEvents, {
      eventId: event.eventId,
    })

    if (existingEventIndex === -1) {
      const newEvent = {
        ...event,
        lastVisitedTime: new Date().toISOString(),
      }
      setRecentEvents((prev) => uniqBy([newEvent, ...prev], "eventId"))
    } else {
      const updatedEvents = [...recentEvents]
      updatedEvents[existingEventIndex] = {
        ...updatedEvents[existingEventIndex],
        lastVisitedTime: new Date().toISOString(),
      }
      const sortedEvents = orderBy(updatedEvents, ["lastVisitedTime"], ["desc"])
      setRecentEvents(uniqBy(sortedEvents, "eventId"))
    }
  }

  const fetchEventDetails = async (isBackground = false) => {
    if (!eventId) {
      console.log("Calling event details without eventID, exiting the function")
      return
    }
    if (isLoading && !isBackground) return
    if (!isBackground) {
      setState((prev) => ({ ...prev, isLoading: true }))
    }

    try {
      const res: any = await getEventDetails({ eventId, uid })
      if (res.success) {
        const eventData = get(res, "data", {})
        const eventMode = getEventModeFromData(eventData)

        if (eventData?.eventDocData?.eventStatus === EVENT_STATUS.OPEN) {
          handleAddEventToRecent({
            ...eventData,
            eventId: get(res, "data.eventId"),
          })
        }

        if (isCreateEvent) {
          // Update both cache and state
          setCache((prev) => {
            const existingCache = prev[eventId]
            const existingEnabledPlugins =
              existingCache?.eventDetails?.eventDocData?.enabled_plugins

            console.log(
              "[useFetchEventDetails] Create event - merging plugins:",
              {
                eventId,
                existingEnabledPlugins:
                  existingEnabledPlugins?.map((p: any) => p.id) || [],
                apiEnabledPlugins:
                  eventData.eventDocData?.enabled_plugins?.map(
                    (p: any) => p.id,
                  ) || [],
                isBackground,
              },
            )

            // For background fetches, preserve existing enabled_plugins to avoid overwriting optimistic updates
            // For foreground fetches, use API data as the source of truth
            const shouldPreserveExisting =
              isBackground &&
              existingEnabledPlugins &&
              existingEnabledPlugins.length > 0

            // Merge API data with existing cache data to preserve local changes
            const mergedEventData = {
              ...eventData,
              eventDocData: {
                ...eventData.eventDocData,
                // Preserve existing enabled_plugins if this is a background fetch and we have existing plugins
                // Otherwise use API data or empty array
                enabled_plugins: shouldPreserveExisting
                  ? existingEnabledPlugins
                  : eventData.eventDocData?.enabled_plugins || [],
              },
            }

            console.log(
              "[useFetchEventDetails] Create event - final merged plugins:",
              {
                eventId,
                finalEnabledPlugins:
                  mergedEventData.eventDocData?.enabled_plugins?.map(
                    (p: any) => p.id,
                  ) || [],
                shouldPreserveExisting,
                isBackground,
              },
            )

            return {
              ...prev,
              [eventId]: {
                eventDetails: mergedEventData,
                eventType: eventMode,
                hasError: false,
              },
            }
          })

          // Use the same merged data for state to ensure consistency
          const existingCache = cache[eventId]
          const existingEnabledPlugins =
            existingCache?.eventDetails?.eventDocData?.enabled_plugins

          console.log("[useFetchEventDetails] Create event - state update:", {
            eventId,
            existingEnabledPlugins:
              existingEnabledPlugins?.map((p: any) => p.id) || [],
            apiEnabledPlugins:
              eventData.eventDocData?.enabled_plugins?.map((p: any) => p.id) ||
              [],
            isBackground,
          })

          // Use the same logic as cache update for consistency
          const shouldPreserveExisting =
            isBackground &&
            existingEnabledPlugins &&
            existingEnabledPlugins.length > 0

          const mergedEventData = {
            ...eventData,
            eventDocData: {
              ...eventData.eventDocData,
              enabled_plugins: shouldPreserveExisting
                ? existingEnabledPlugins
                : eventData.eventDocData?.enabled_plugins || [],
            },
          }

          console.log(
            "[useFetchEventDetails] Create event - final state plugins:",
            {
              eventId,
              finalEnabledPlugins:
                mergedEventData.eventDocData?.enabled_plugins?.map(
                  (p: any) => p.id,
                ) || [],
              shouldPreserveExisting,
              isBackground,
            },
          )

          setState({
            eventDetails: mergedEventData,
            eventType: eventMode,
            isLoading: false,
            hasError: false,
            doesNotExist: false,
          })
        } else {
          // Update both cache and state
          setCache((prev) => ({
            ...prev,
            [eventId]: {
              eventDetails: eventData,
              eventType: eventMode,
              hasError: false,
            },
          }))

          setState({
            eventDetails: eventData,
            eventType: eventMode,
            isLoading: false,
            hasError: false,
            doesNotExist: false,
          })
        }
      } else if (
        res.message.includes("event restrictions") ||
        res.message.includes("restricted")
      ) {
        setCache((prev) => ({
          ...prev,
          [eventId]: {
            eventDetails: null,
            eventType: EEventStatus.INELIGIBLE,
            hasError: false,
          },
        }))
        setState({
          eventDetails: null,
          eventType: EEventStatus.INELIGIBLE,
          isLoading: false,
          hasError: false,
          doesNotExist: false,
        })
      } else if (res.message.includes("does not exist")) {
        console.log("inside error event not exist ", res.message),
          setState({
            eventDetails: null,
            eventType: null,
            isLoading: false,
            hasError: false,
            doesNotExist: true,
          })
      }
    } catch (error) {
      setState({
        eventDetails: null,
        eventType: null,
        isLoading: false,
        hasError: true,
        doesNotExist: false,
      })
    } finally {
      setState((prev) => ({
        ...prev,
        isLoading: false,
      }))
    }
  }

  return { state, refresh: () => fetchEventDetails(false) }
}

export default useFetchEventDetails
